import type { InputButton, InputState } from '@arcraz/arcadia-types';
import type { InputSource } from './loop.js';

/**
 * Keyboard input source.
 *
 * Maps the cabinet's default key bindings (Arrow keys / WASD / ZXC /
 * Space / 5) into the unified `InputState` per `docs/spec-runtime.md`.
 *
 * Maintains its own held-keys set, snapshots it each tick into an
 * immutable `InputState`, and computes `justPressed`/`justReleased` from
 * the previous frame's snapshot. Edge events are surfaced to cartridges
 * only on the rising/falling tick — held keys stay quiet.
 *
 * Lifecycle: `attach(window)` installs the listeners; `detach()` removes
 * them. The cabinet calls `attach()` once at cartridge launch and
 * `detach()` when the cartridge tears down or the cabinet exits.
 */

const KEY_BUTTON_MAP: ReadonlyMap<string, InputButton> = new Map([
  ['ArrowUp', 'up'],
  ['ArrowDown', 'down'],
  ['ArrowLeft', 'left'],
  ['ArrowRight', 'right'],
  ['KeyW', 'up'],
  ['KeyS', 'down'],
  ['KeyA', 'left'],
  ['KeyD', 'right'],
  ['KeyZ', 'fireA'],
  ['KeyJ', 'fireA'],
  ['KeyX', 'fireB'],
  ['KeyK', 'fireB'],
  ['KeyC', 'fireC'],
  ['KeyL', 'fireC'],
  ['Space', 'start'],
  ['Enter', 'start'],
  ['Digit5', 'coin'],
  // Escape doubles as `coin` — cartridges rarely read coin, and the pause
  // overlay uses coin's justPressed edge to resume. Cabinet-level
  // window listener on Escape fires the pause-request channel separately
  // (see main.ts), so opening + closing the overlay both work with the
  // same key.
  ['Escape', 'coin'],
]);

const EMPTY_SET: ReadonlySet<InputButton> = Object.freeze(new Set<InputButton>());

const ZERO_STICK = Object.freeze({ x: 0, y: 0 });

export class KeyboardInputSource implements InputSource {
  private held: Set<InputButton> = new Set();
  private prevHeld: Set<InputButton> = new Set();
  private attached = false;
  private win: Window | null = null;
  private stickL: { x: number; y: number } = { x: 0, y: 0 };

  attach(win: Window = globalThis.window): void {
    if (this.attached) {
      return;
    }
    this.win = win;
    this.attached = true;
    win.addEventListener('keydown', this.handleKeyDown);
    win.addEventListener('keyup', this.handleKeyUp);
    win.addEventListener('blur', this.handleBlur);
  }

  detach(): void {
    if (!this.attached || !this.win) {
      return;
    }
    this.win.removeEventListener('keydown', this.handleKeyDown);
    this.win.removeEventListener('keyup', this.handleKeyUp);
    this.win.removeEventListener('blur', this.handleBlur);
    this.attached = false;
    this.win = null;
    this.held.clear();
    this.prevHeld.clear();
  }

  /**
   * Snapshot the current frame's input. The returned object captures the
   * current held set + edge events derived from the previous snapshot.
   * After returning, `prevHeld` is updated for the next call.
   *
   * Called once per fixed tick by the runtime loop.
   */
  snapshot(): InputState {
    const justPressed = diff(this.held, this.prevHeld);
    const justReleased = diff(this.prevHeld, this.held);
    const stickL =
      this.stickL.x === 0 && this.stickL.y === 0
        ? ZERO_STICK
        : { x: this.stickL.x, y: this.stickL.y };
    const state: InputState = {
      up: this.held.has('up'),
      down: this.held.has('down'),
      left: this.held.has('left'),
      right: this.held.has('right'),
      fireA: this.held.has('fireA'),
      fireB: this.held.has('fireB'),
      fireC: this.held.has('fireC'),
      start: this.held.has('start'),
      coin: this.held.has('coin'),
      stickL,
      stickR: ZERO_STICK,
      justPressed,
      justReleased,
    };
    // Snapshot held set for next-frame edge diff.
    this.prevHeld = new Set(this.held);
    return state;
  }

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (isEditableTarget(e.target)) return;
    const btn = KEY_BUTTON_MAP.get(e.code);
    if (btn === undefined) {
      return;
    }
    // Prevent the browser from scrolling on Arrow/Space during gameplay.
    if (
      e.code.startsWith('Arrow') ||
      e.code === 'Space' ||
      e.code === 'KeyW' ||
      e.code === 'KeyA' ||
      e.code === 'KeyS' ||
      e.code === 'KeyD'
    ) {
      e.preventDefault();
    }
    this.held.add(btn);
  };

  private handleKeyUp = (e: KeyboardEvent): void => {
    if (isEditableTarget(e.target)) return;
    const btn = KEY_BUTTON_MAP.get(e.code);
    if (btn === undefined) {
      return;
    }
    this.held.delete(btn);
  };

  /**
   * Release all keys when the window loses focus. Without this, if a key
   * is held while the user alt-tabs away, `keyup` never fires and the
   * player keeps "holding" the direction forever.
   */
  private handleBlur = (): void => {
    this.held.clear();
  };

  /**
   * Programmatic press — used by the touch-overlay (`input-touch.ts`) to
   * feed virtual button events into the same held set the keyboard
   * manipulates. Single snapshot, no merging logic. Touch + keyboard
   * presses are additive: holding D-pad LEFT while also pressing the
   * arrow key just keeps `left` in `held`; releasing one of the two
   * leaves the other still pressed.
   */
  pressButton(btn: InputButton): void {
    this.held.add(btn);
  }

  releaseButton(btn: InputButton): void {
    this.held.delete(btn);
  }

  /**
   * Set the left stick's analog value. x/y in [-1, 1]. Y is "screen
   * down positive" by convention (so a joystick dragged down sends y=+1).
   * Used by the touch overlay's virtual joystick. Keyboard input doesn't
   * call this — Arrow / WASD remain digital-only.
   */
  setStickL(x: number, y: number): void {
    this.stickL.x = x;
    this.stickL.y = y;
  }
}

/**
 * True when the event originated inside a text-editing element (login
 * modal email field, future settings inputs, etc.). The cabinet input
 * source ignores keys from those so the player can type without the
 * cabinet stealing keystrokes — z would map to fireA and launch a
 * cartridge mid-email otherwise.
 */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

function diff(
  a: ReadonlySet<InputButton>,
  b: ReadonlySet<InputButton>
): ReadonlySet<InputButton> {
  let result: Set<InputButton> | null = null;
  for (const btn of a) {
    if (!b.has(btn)) {
      if (!result) {
        result = new Set();
      }
      result.add(btn);
    }
  }
  return result ?? EMPTY_SET;
}
